<script lang="ts">
	import { type NavLink } from '$lib/utils/url.util';
	import IconChevronLeft from '~icons/mdi/chevron-left';
	import IconChevronRight from '~icons/mdi/chevron-right';

	const {
		currentPage,
		totalPages,
		constructNewNav,
		doOnPreviousPageChange,
		doOnSpecificPageChange,
		doOnNextPageChange
	}: {
		currentPage: number;
		totalPages: number;
		constructNewNav: (page: number) => NavLink;
		doOnPreviousPageChange: (navigatingToPage: number) => void;
		doOnSpecificPageChange: (navigatingToPage: number) => void;
		doOnNextPageChange: (navigatingToPage: number) => void;
	} = $props();

	const labels = $derived<(number | string)[]>(generatePageLabels(currentPage, totalPages));

	/**
	 * Generates an array of labels for the pagination, e.g. [1, 2, 3, 4, 5, '...', 10]
	 * @param current current page
	 * @param total total pages
	 * @returns
	 */
	function generatePageLabels(current: number, total: number): (number | string)[] {
		if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

		const pages: (number | string)[] = [1];
		if (current > 3) pages.push('...');

		const start = Math.max(2, current - 1);
		const end = Math.min(total - 1, current + 1);

		for (let i = start; i <= end; i++) {
			pages.push(i);
		}

		if (current < total - 2) pages.push('...');
		pages.push(total);

		return pages;
	}
</script>

<div class="mt-12 flex justify-center">
	<div class="join rounded-field shadow-sm">
		{@render PreviousPageBtn()}
		{#each labels as label (label)}
			{const isCurrentPage = $derived<boolean>(currentPage === label)}
			{#if typeof label === 'number'}
				{@render PageBtn(Number(label), isCurrentPage)}
			{:else}
				<button class="btn btn-disabled join-item cursor-not-allowed btn-primary">...</button>
			{/if}
		{/each}
		{@render NextPageBtn()}
	</div>
</div>

{#snippet PreviousPageBtn()}
	{const isFirstPage = $derived<boolean>(currentPage === 1)}
	{const nav = $derived<NavLink>(constructNewNav(currentPage - 1))}
	<a
		class={{
			'btn join-item btn-primary': true,
			'btn-disabled cursor-not-allowed': isFirstPage,
			'btn-ghost': !isFirstPage
		}}
		href={currentPage > 1 ? `/${nav.href}` : '#'}
		data-sveltekit-preload-data={nav.preloadData}
		data-sveltekit-preload-code={nav.preloadCode}
		data-sveltekit-noscroll
		aria-label="Previous Page"
		title="Previous Page"
		aria-disabled={isFirstPage}
		onclick={() => {
			if (isFirstPage) return;
			doOnPreviousPageChange(currentPage - 1);
		}}
	>
		<IconChevronLeft class="text-lg" />
	</a>
{/snippet}

{#snippet PageBtn(pageNum: number, isCurrentPage: boolean)}
	{const nav = $derived<NavLink>(constructNewNav(pageNum))}
	<a
		class={{
			'btn join-item btn-primary': true,
			'btn-active': isCurrentPage,
			'btn-ghost': !isCurrentPage
		}}
		href={`/${nav.href}`}
		data-sveltekit-preload-data={nav.preloadData}
		data-sveltekit-preload-code={nav.preloadCode}
		data-sveltekit-noscroll
		aria-label="Page {pageNum}"
		title="Page {pageNum}"
		aria-current={currentPage === pageNum ? 'page' : undefined}
		onclick={() => {
			doOnSpecificPageChange(pageNum);
		}}
	>
		{pageNum}
	</a>
{/snippet}

{#snippet NextPageBtn()}
	{const isLastPage = $derived<boolean>(currentPage === totalPages)}
	{const nav = $derived<NavLink>(constructNewNav(currentPage + 1))}
	<a
		class={{
			'btn join-item btn-primary': true,
			'btn-disabled cursor-not-allowed': isLastPage,
			'btn-ghost': !isLastPage
		}}
		href={currentPage < totalPages ? `/${nav.href}` : '#'}
		data-sveltekit-preload-data={nav.preloadData}
		data-sveltekit-preload-code={nav.preloadCode}
		data-sveltekit-noscroll
		aria-label="Next Page"
		title="Next Page"
		aria-disabled={isLastPage}
		onclick={() => {
			if (isLastPage) return;
			doOnNextPageChange(currentPage + 1);
		}}
	>
		<IconChevronRight class="text-lg" />
	</a>
{/snippet}
